import { Checkbox } from '../ui/checkbox'
import { Dialog } from '../ui/dialog'
import { Classes, ControlGroup, Icon } from '@blueprintjs/core'
import { Menu, MenuDivider, MenuItem } from '../ui/menu'
import { HTMLSelect } from '../ui/html-select'
import { Popover, Classes as PopoverClasses } from '../ui/popover'
import { Tooltip } from '../ui/tooltip'
import classNames, { ClassValue } from 'classnames'
import { differenceInCalendarDays, differenceInCalendarMonths, format, parse } from 'date-fns'
import { Dictionary, keyBy } from 'lodash'
import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { DefaultNumericOperators } from '../../config'
import { FilterI, FilterValue, Operator } from '../../constants/filter'
import { FilterIcon } from '../../icons/icons'
import { EditableField } from '../../packages/blueprint-components/src/EditableField'
import { FilterDate } from '../../packages/blueprint-components/src/FilterDate'
import { FilterNumber } from '../../packages/blueprint-components/src/FilterNumber'
import { MyHTMLTable } from '../../packages/blueprint-components/src/HTMLTable'
import { SavedFilter } from '../../packages/blueprint-components/src/useFilter'
import { UserStore } from '../../stores/user_store'
import { Column } from '../../types/table'
import { type EntityId, idKey } from '../../types/constants'
import { axiosDestroy, axiosGet, axiosPatch } from '../../util/api'
import { removeIncompleteSelectComponentFilters } from '../../util/transformSelectComponentFilters'
import { FilterKeyName } from '../FilterTags'
import { TextInput } from '../inputs/Input'
import { Toasts } from '../toaster/Toast'
import { TableStore } from './table_store'
import { Button } from '../ui/button'

interface Filter3Props {
    className?: ClassValue
    store: NewFilterStore
    allowSavedFilters?: boolean
}

export class SavedFiltersStore {
    userStore: UserStore
    /** An array of SavedFilters, keyed by the profile id, then the table name */
    savedFilters: Record<string, Dictionary<SavedFilter[]>> = {}
    loaded: Record<string, Dictionary<boolean>> = {}
    loading: Record<string, Dictionary<boolean>> = {}
    reloadQueued: Record<string, Dictionary<boolean>> = {}
    lastTableName: string | null = null
    resetVersion = 0

    constructor(userStore: UserStore) {
        this.userStore = userStore
        makeAutoObservable(this)

        reaction(
            () => this.userStore.profileId,
            (profileId) => {
                if (profileId === null) {
                    this.reset()
                } else if (this.lastTableName) {
                    this.tryLoad(this.lastTableName)
                }
            },
        )
    }

    setLoading(profileId: EntityId, tableName: string, loading: boolean) {
        const profileKey = idKey(profileId)
        if (!this.loading[profileKey]) {
            this.loading[profileKey] = {}
        }
        this.loading[profileKey][tableName] = loading
    }

    setLoaded(profileId: EntityId, tableName: string, loaded: boolean) {
        const profileKey = idKey(profileId)
        if (!this.loaded[profileKey]) {
            this.loaded[profileKey] = {}
        }
        this.loaded[profileKey][tableName] = loaded
    }

    setSavedFilters(profileId: EntityId, tableName: string, filters: SavedFilter[]) {
        const profileKey = idKey(profileId)
        if (!this.savedFilters[profileKey]) {
            this.savedFilters[profileKey] = {}
        }
        this.savedFilters[profileKey][tableName] = filters
    }

    setReloadQueued(profileId: EntityId, tableName: string, queued: boolean) {
        const profileKey = idKey(profileId)
        if (!this.reloadQueued[profileKey]) {
            this.reloadQueued[profileKey] = {}
        }
        this.reloadQueued[profileKey][tableName] = queued
    }

    filtersFor(tableName: string): SavedFilter[] {
        const profileId = this.userStore.profileId
        return profileId ? (this.savedFilters[idKey(profileId)]?.[tableName] ?? []) : []
    }

    reset() {
        this.resetVersion += 1
        this.savedFilters = {}
        this.loaded = {}
        this.loading = {}
        this.reloadQueued = {}
        this.lastTableName = null
    }

    async tryLoad(tableName: string) {
        this.lastTableName = tableName
        const profileId = this.userStore.profileId
        const profileKey = idKey(profileId)
        if (
            !profileId ||
            this.loaded[profileKey]?.[tableName] ||
            this.loading[profileKey]?.[tableName]
        ) {
            return
        }

        return this.load(tableName)
    }

    async load(tableName: string) {
        const profileId = this.userStore.profileId
        if (!profileId) {
            return
        }
        return this.loadForProfile(profileId, tableName)
    }

    private async loadForProfile(profileId: EntityId, tableName: string) {
        const profileKey = idKey(profileId)
        if (this.loading[profileKey]?.[tableName]) {
            return
        }
        while (true) {
            const resetVersion = this.resetVersion
            this.setLoading(profileId, tableName, true)
            try {
                const res = await axiosGet('custom-filters', { tableName })
                if (this.resetVersion === resetVersion) {
                    runInAction(() => {
                        this.setSavedFilters(profileId, tableName, res.data)
                        this.setLoaded(profileId, tableName, true)
                        this.setLoading(profileId, tableName, false)
                    })
                }
            } catch (err) {
                console.error('Error loading saved filters', err)
                if (this.resetVersion === resetVersion) {
                    runInAction(() => {
                        this.setLoading(profileId, tableName, false)
                    })
                }
            }

            if (this.resetVersion !== resetVersion || !this.reloadQueued[profileKey]?.[tableName]) {
                return
            }
            this.setReloadQueued(profileId, tableName, false)
        }
    }

    private refreshForProfile(profileId: EntityId, tableName: string) {
        if (this.loading[idKey(profileId)]?.[tableName]) {
            this.setReloadQueued(profileId, tableName, true)
            return
        }
        return this.loadForProfile(profileId, tableName)
    }

    async save(tableName: string, filterName: string, filters: Dictionary<FilterValue>) {
        const profileId = this.userStore.profileId
        return axiosPatch('custom-filters', {
            name: filterName,
            tableName,
            filters,
        })
            .then((_) => {
                Toasts.success(`${filterName} saved`)
                if (profileId) {
                    return this.refreshForProfile(profileId, tableName)
                }
            })
            .catch((err) => {
                Toasts.error(err.message)
                throw err
            })
    }

    async delete(savedFilter: SavedFilter) {
        const profileId = this.userStore.profileId
        if (!profileId) {
            console.warn('No profile ID available')
            return
        }

        return axiosDestroy(`custom-filters/${savedFilter.id}`)
            .then(() => {
                Toasts.success(`${savedFilter.name} deleted`)
                return this.refreshForProfile(profileId, savedFilter.table_name)
            })
            .catch((err) => {
                Toasts.error(err.message)
            })
    }

    async edit({ id, filters, table_name, name }: SavedFilter) {
        const profileId = this.userStore.profileId
        return axiosPatch('custom-filters', {
            id,
            name,
            tableName: table_name,
            filters,
        }).then(() => {
            Toasts.success(`Filter saved`)
            if (profileId) {
                return this.refreshForProfile(profileId, table_name)
            }
        })
    }
}

class FavoriteFilterStore {
    favorites: Dictionary<boolean>

    constructor() {
        const stored = window.localStorage.getItem('favorites')
        this.favorites = stored ? JSON.parse(stored) : {}

        makeAutoObservable(this)
    }

    isFavorite(filter: FilterI) {
        return this.favorites[filter.key]
    }

    toggle(filter: FilterI) {
        this.favorites[filter.key] = !this.favorites[filter.key]
        window.localStorage.setItem('favorites', JSON.stringify(this.favorites))
    }
}

export class NewFilterStore {
    open = false
    tableStore: TableStore<any>
    allowedFilters: FilterI[]
    activeFilters: Dictionary<FilterValue>
    dirtyFilters: Dictionary<FilterValue | undefined> = {}
    search = ''
    favorites: FavoriteFilterStore
    savedFiltersStore: SavedFiltersStore
    savedFiltersTableName: string

    showManageModal = false
    showSaveModal = false

    constructor(
        tableStore: TableStore<any>,
        savedFiltersStore: SavedFiltersStore,
        allowedFilters: FilterI[],
        syncWithTableStore = true,
        savedFiltersTableName = tableStore.tableName,
    ) {
        this.tableStore = tableStore
        this.allowedFilters = allowedFilters
        this.activeFilters = tableStore.params.filter || {}
        this.favorites = new FavoriteFilterStore()
        this.savedFiltersStore = savedFiltersStore
        this.savedFiltersTableName = savedFiltersTableName

        makeAutoObservable(this)

        this.savedFiltersStore.tryLoad(this.savedFiltersTableName)

        // Update the table store's filters when we change them
        if (syncWithTableStore) {
            reaction(
                () => this.activeFilters,
                (filters) => {
                    this.tableStore.setFilters(filters)
                },
            )
        }

        // Update the local filters when the profile changes
        reaction(
            () => this.tableStore.userStore.profileId,
            (profileId) => {
                this.activeFilters = this.tableStore.params.filter || {}
            },
        )
    }

    setOpen(open: boolean) {
        this.open = open
    }

    get disabled() {
        return this.tableStore.loading
    }

    /**
     * Returns the current filters, including active and dirty filters
     *
     * @return {Dictionary<FilterValue>}
     */
    get currentFilters() {
        const filters = {
            ...this.activeFilters,
            ...this.dirtyFilters,
        }
        return Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== undefined),
        )
    }

    isActive(filter: FilterI) {
        return this.currentFilters[filter.key] !== undefined
    }

    toggle(filter: FilterI) {
        return (e: ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked
            this.dirtyFilters[filter.key] = checked ? defaultValue(filter) : undefined
        }
    }

    get get() {
        return (filter: FilterI) => {
            return this.currentFilters[filter.key]
        }
    }

    set(filter: FilterI, value: FilterValue) {
        this.dirtyFilters[filter.key] = value
    }

    clear() {
        this.dirtyFilters = {}
        this.activeFilters = {}
        this.setOpen(false)
    }

    apply() {
        this.activeFilters = this.currentFilters
        this.dirtyFilters = {}
        this.setOpen(false)
    }

    setSearch(search: string) {
        this.search = search
    }

    get shownFilters() {
        return this.allowedFilters.filter((filter) =>
            filter.name.toLowerCase().includes(this.search.toLowerCase()),
        )
    }

    get favoriteFilters() {
        return this.shownFilters.filter((filter) => this.favorites.isFavorite(filter))
    }

    get notFavoriteFilters() {
        return this.shownFilters.filter((filter) => !this.favorites.isFavorite(filter))
    }

    get isFavorite() {
        return (filter: FilterI) => this.favorites.isFavorite(filter)
    }

    get savedFilters() {
        return this.savedFiltersStore.filtersFor(this.savedFiltersTableName)
    }

    setShowManageModal(show: boolean) {
        this.showManageModal = show
    }

    setShowSaveModal(show: boolean) {
        this.showSaveModal = show
    }

    get saveable() {
        return Object.keys(this.currentFilters).length > 0
    }

    applySavedFilter(savedFilter: SavedFilter) {
        this.activeFilters = removeIncompleteSelectComponentFilters(savedFilter.filters) ?? {}
    }

    saveCustomFilter(filterName: string, filters: Dictionary<FilterValue>) {
        this.savedFiltersStore.save(this.savedFiltersTableName, filterName, filters).then(() => {
            runInAction(() => {
                this.apply()
                this.setOpen(false)
                this.setShowSaveModal(false)
            })
        })
    }

    remove(filterName: string) {
        this.activeFilters = Object.fromEntries(
            Object.entries(this.activeFilters).filter(([key]) => key !== filterName),
        )
    }

    get filterCount() {
        return Object.keys(this.activeFilters).length
    }

    get allowedFiltersByKey() {
        return this.allowedFilters.reduce((acc, filter) => {
            acc[filter.key] = filter
            return acc
        }, {})
    }
}

export const Filter3 = ({ className, store, allowSavedFilters = true }: Filter3Props) => {
    return (
        <div className={classNames('d-flex align-items-center', className)}>
            <FilterButton store={store} allowSavedFilters={allowSavedFilters} />
            <FilterTags store={store} />
            <SavedFilterModal store={store} />
            <ManageFilterModal store={store} />
        </div>
    )
}

const FilterButton = observer(
    ({
        store,
        allowSavedFilters,
        editMode = false,
        scrollable = false,
        onSave = () => {},
    }: {
        store: NewFilterStore
        allowSavedFilters: boolean
        /**
         * Edit mode is intended to allow a user to mutate an existing
         * saved filter.
         */
        editMode?: boolean
        scrollable?: boolean
        onSave?: () => void
    }) => {
        return (
            <Popover
                position="bottom"
                content={
                    <FilterMenu
                        store={store}
                        allowSavedFilters={allowSavedFilters}
                        editMode={editMode}
                        scrollable={scrollable}
                        onSave={() => onSave()}
                    />
                }
                isOpen={store.open}
                interactionKind="click"
                onClose={() => store.setOpen(false)}
            >
                <Button
                    className="rounded-lg mr-2"
                    minimal
                    outlined
                    disabled={store.disabled}
                    icon={<FilterIcon />}
                    text={'Filter'}
                    onClick={() => store.setOpen(true)}
                />
            </Popover>
        )
    },
)

const FilterMenu = observer(
    ({
        store,
        scrollable = false,
        allowSavedFilters,
        editMode = false,
        onSave = () => {},
    }: {
        store: NewFilterStore
        scrollable?: boolean
        allowSavedFilters: boolean
        editMode?: boolean
        onSave?: () => void
    }) => {
        const className = scrollable ? 'overflow-scroll overflow-x-hidden h-80' : ''

        return (
            <Menu style={{ width: 300 }}>
                {/* Actions */}
                <div className="flex w-full justify-between">
                    {editMode && (
                        <MenuItem
                            icon="tick"
                            text="Save"
                            onClick={() => {
                                store.apply()
                                onSave()
                            }}
                        />
                    )}
                    {!editMode && (
                        <>
                            <MenuItem icon="cross" text="Clear" onClick={() => store.clear()} />
                            <MenuItem icon="tick" text="Apply" onClick={() => store.apply()} />
                        </>
                    )}
                </div>
                <MenuDivider />

                {/* Saved filter submenu */}
                {allowSavedFilters && (
                    <>
                        <MenuItem text="Saved Filters" popoverProps={{ openOnTargetFocus: false }}>
                            <MenuItem
                                disabled={store.savedFilters.length === 0}
                                icon="wrench"
                                text="Manage"
                                onClick={() => store.setShowManageModal(true)}
                            />
                            <MenuItem
                                disabled={!store.saveable}
                                icon="floppy-disk"
                                text="Save"
                                onClick={() => store.setShowSaveModal(true)}
                            />
                            <MenuDivider />

                            {store.savedFilters.map((filter) => (
                                <MenuItem
                                    key={filter.id}
                                    text={filter.name}
                                    label={
                                        filter?.updated_at
                                            ? format(new Date(filter.updated_at), 'M/d/yy')
                                            : null
                                    }
                                    onClick={() => store.applySavedFilter(filter)}
                                />
                            ))}
                        </MenuItem>
                        <MenuDivider />
                    </>
                )}

                {/* Search */}
                <div className="mx-1 border-b">
                    <TextInput
                        autoFocus
                        className="mb-2 mt-1"
                        value={store.search}
                        onChange={(v) => store.setSearch(v)}
                        placeholder="Search"
                        onKeyDown={(e) => (e.key === 'Enter' ? store.apply() : null)}
                    />
                    {!!store.search && (
                        <div className="text-gray-300 mb-2 text-xs">
                            Showing {store.shownFilters.length} / {store.allowedFilters.length}{' '}
                            filters
                        </div>
                    )}
                </div>

                {/* Actual Filter List */}
                <div className={className}>
                    {store.favoriteFilters.length > 0 && (
                        <>
                            <div className="mt-2" />
                            <div className="favorites-section">
                                <div className="text-xs text-gray-500 px-3 py-1 font-medium">
                                    Favorites
                                </div>
                                {store.favoriteFilters.map((filter) => (
                                    <FilterMenuItem
                                        key={filter.key}
                                        filter={filter}
                                        store={store}
                                    />
                                ))}
                            </div>
                            <div className="border-b my-2" />
                        </>
                    )}
                    <div>
                        {store.notFavoriteFilters.map((filter) => (
                            <FilterMenuItem key={filter.key} filter={filter} store={store} />
                        ))}
                    </div>
                </div>
            </Menu>
        )
    },
)

const FilterMenuItem = observer(({ filter, store }: { filter: FilterI; store: NewFilterStore }) => {
    return (
        <div className="bp4-menu-item group">
            <div className="flex flex-col w-100">
                <div className="flex justify-between gap-2">
                    <Checkbox
                        className="mb-0"
                        checked={store.isActive(filter)}
                        onChange={store.toggle(filter)}
                    />
                    <div className="flex-grow text-left">{filter.name}</div>
                    <Icon
                        className={classNames(
                            'ml-2 transition-opacity duration-200 opacity-0 group-hover:opacity-100',
                            {
                                'text-gray-300': !store.isFavorite(filter),
                                'text-yellow-300': store.isFavorite(filter),
                            },
                        )}
                        icon={store.isFavorite(filter) ? 'star' : 'star-empty'}
                        onClick={() => store.favorites.toggle(filter)}
                    />
                </div>
                <FilterMenuInput filter={filter} store={store} />
            </div>
        </div>
    )
})

const FilterMenuInput = observer(
    ({ filter, store }: { filter: FilterI; store: NewFilterStore }) => {
        if (!store.isActive(filter)) {
            return null
        }

        switch (filter.type) {
            case 'select':
                return (
                    // No `bp4-select` wrapper: its Blueprint rules move the
                    // caret of the shared HTMLSelect out of the box.
                    <HTMLSelect
                        className="mt-2"
                        fill
                        // @ts-ignore
                        value={store.get(filter)}
                        onChange={(e) => store.set(filter, e.target.value)}
                        options={filter.options.map((option) => ({
                            value: option.value,
                            label: option.name,
                        }))}
                    />
                )
            case 'date':
                return (
                    <FilterDate
                        filter={filter}
                        // @ts-ignore
                        data={store.get(filter)}
                        onChange={(payload) => store.set(filter, payload)}
                    />
                )
            case 'number':
            case 'nullable-number':
                return (
                    <FilterNumber
                        filter={filter}
                        // @ts-ignore
                        value={store.get(filter)}
                        onChange={(payload) => store.set(filter, payload)}
                        nullable={filter.type === 'nullable-number'}
                    />
                )
            case 'select-component':
                const Selector = filter.component
                return (
                    <Selector
                        className="mt-2"
                        // @ts-ignore
                        value={store.get(filter)}
                        onChange={(payload) => store.set(filter, payload)}
                    />
                )
            default:
                console.error('Cannot renderInput for filter type', filter)
                return null
        }
    },
)

const TAGS_MAX_WIDTH = 435
const FilterTags = observer(({ store }: { store: NewFilterStore }) => {
    const divRef = useRef<HTMLDivElement>(null)

    const [hiddenTagsCount, setHiddenTagsCount] = useState<number>(0)

    const _removeActiveFilter = (activeFilter: FilterKeyName) => {
        store.remove(activeFilter as string)
    }

    const moreFilterPopup = useMemo(() => {
        const filterTags = (
            <ul>
                {(Object.keys(store.activeFilters) as FilterKeyName[])
                    .slice(store.filterCount - hiddenTagsCount)
                    .map((filterKeyName, index) => (
                        <li className="my-1.5" key={index}>
                            <FilterTag store={store} filterKeyName={filterKeyName} />
                        </li>
                    ))}
            </ul>
        )

        if (store.filterCount > store.filterCount - hiddenTagsCount) {
            return (
                <Popover
                    popoverClassName={PopoverClasses.POPOVER2_CONTENT_SIZING}
                    content={filterTags}
                    interactionKind="click"
                    position="bottom"
                >
                    <span className="underline text-xs">
                        +{hiddenTagsCount} more {hiddenTagsCount == 1 ? 'filter' : 'filters'}
                    </span>
                </Popover>
            )
        }
    }, [store.filterCount, hiddenTagsCount, store.activeFilters, store.tableStore.loading])

    const generateTag = ({ filterKeyName }: { filterKeyName: FilterKeyName }) => {
        return <FilterTag store={store} filterKeyName={filterKeyName} />
    }

    // manages the show/hide state of the tags
    useEffect(() => {
        const div = divRef.current
        if (!div) {
            return
        }

        const containerWidth = div.clientWidth
        const tagElements = div.querySelectorAll('._unique_filter_tags')
        let hiddenCount = 0

        // Find the index of the tag that is overflowing the container
        /** @ts-ignore */
        for (const tagElement of tagElements) {
            const parentRect = div.getBoundingClientRect() // get parent position
            const tagRect = tagElement.getBoundingClientRect() // get child position

            if (tagRect.right - parentRect.left + 85 > containerWidth) {
                hiddenCount = tagElements.length - parseInt(tagElement.dataset.tagIndex)
                break
            }
        }

        // Set the hidden count. We want to hide all tags after the first overflowing tag,
        // including the tag that is overflowing
        setHiddenTagsCount(hiddenCount)
    }, [store.activeFilters])

    return (
        <div className="flex">
            <div
                ref={divRef}
                className="flex overflow-hidden"
                style={{ width: TAGS_MAX_WIDTH, maxWidth: TAGS_MAX_WIDTH }}
            >
                {(Object.keys(store.activeFilters) as FilterKeyName[]).map(
                    (filterKeyName: FilterKeyName, index) => {
                        const shouldHide =
                            index >= Object.keys(store.activeFilters).length - hiddenTagsCount
                        return (
                            <div
                                className={classNames('_unique_filter_tags', {
                                    hidden: shouldHide,
                                })}
                                data-tag-index={index}
                                key={index}
                            >
                                {generateTag({ filterKeyName })}
                            </div>
                        )
                    },
                )}
                {moreFilterPopup}
            </div>
        </div>
    )
})

const FilterTag = observer(
    ({ store, filterKeyName }: { store: NewFilterStore; filterKeyName: FilterKeyName }) => {
        const loading = store.tableStore.loading
        const value = store.activeFilters[filterKeyName]
        const filter = store.allowedFiltersByKey[filterKeyName]
        const filterName = filter?.name ?? filterKeyName
        const resolvedValue = resolveValue(value, filter)

        const tag = (verbiage: string, index: number) => (
            <span
                key={index}
                className="bp4-tag bp4-minimal bp4-interactive bg-white border border-mjDark-600 mr-2"
            >
                <span
                    onClick={() => store.setOpen(true)}
                    className="bp4-fill bp4-text-overflow-ellipsis text-mjDark-800 text-xs font-normal"
                >
                    {verbiage}
                </span>
                <button
                    disabled={loading}
                    onClick={() => store.remove(filterKeyName)}
                    className="bp4-tag-remove"
                >
                    <Icon icon="small-cross" color="#2B4E75" />
                </button>
            </span>
        )

        if (typeof value == 'string' || typeof value == 'number') {
            return tag(`${filterName}: ${resolvedValue}`, 0)
        } else if (typeof value == 'object') {
            // we use this for filters with operators i.e {">": 5}
            // @ts-ignore
            if (value.type == 'select-component') {
                const name =
                    // @ts-ignore
                    value?.name?.length > 13 ? value.name.slice(0, 10) + '...' : value?.name
                // @ts-ignore
                if (value?.name?.length > 13) {
                    return (
                        <span>
                            {/* @ts-ignore */}
                            <Tooltip key={0} position="top" content={value?.name ?? 'undefined'}>
                                {tag(`${filterName} = ${name}`, 0)}
                            </Tooltip>
                        </span>
                    )
                }

                return tag(`${filterName} = ${name}`, 0)
            }

            // Some filters may support multiple operators,
            // so the loop below handles the various operators available.
            const final = []
            Object.keys(value).forEach((operator, index) => {
                // tThis gets used for 95% of the filters
                if (DefaultNumericOperators.includes(operator as Operator)) {
                    final.push(tag(`${filterName} ${operator} ${value[operator]}`, index))
                    return
                }

                let output = null
                let v = store.activeFilters[filterKeyName][operator]
                const period =
                    filterKeyName == 'start_date' || filterKeyName == 'end_date'
                        ? 'period' in value
                            ? value.period
                            : undefined
                        : null

                if (v == 'select-component') {
                    const t = value[operator]
                    const val = t.length > 13 ? t.slice(0, 10) + '...' : t
                    final.push(tag(`${filterName} = ${val}`, index))
                    return
                }

                let overrideOperator
                if (operator == 'last') {
                    overrideOperator = 'is in the last'
                    const z = parse(v, 'yyyy-MM-dd', new Date())
                    if (period == 'days') {
                        v = Math.abs(differenceInCalendarDays(z, new Date()))
                    } else if (period == 'months') {
                        v = Math.abs(differenceInCalendarMonths(z, new Date()))
                    }
                } else if (operator == 'period') {
                    return
                }

                const suffix = operator == 'last' && period ? ` ${period}` : ''
                if (overrideOperator != '') {
                    output = tag(`${filterName} ${overrideOperator} ${v}${suffix}`, index)
                } else {
                    const name =
                        // @ts-ignore
                        value?.name?.length > 13 ? value.name.slice(0, 10) + '...' : value?.name
                    output = tag(`${filterName}: ${name ?? 'undefined'}`, index)
                }

                // @ts-ignore
                if (value?.name?.length > 13) {
                    final.push(
                        // @ts-ignore
                        <Tooltip key={index} position="top" content={value?.name ?? 'undefined'}>
                            {output}
                        </Tooltip>,
                    )
                } else {
                    final.push(output)
                }
            })
            return <>{final}</>
        }
    },
)

export const resolveValue = (value, filter) => {
    if (filter?.type === 'select') {
        const keyedOptions = keyBy(filter.options, 'value')
        if (keyedOptions[value]?.name) {
            return keyedOptions[value].name
        }
    }

    return `${filter?.prefix ?? ''}${value}${filter?.suffix ?? ''}`
}

/**
 * Returns the default value for a new filter
 *
 * @param {FilterI} filter
 * @return {FilterValue}
 */
const defaultValue = (filter: FilterI): FilterValue => {
    switch (filter.type) {
        case 'select':
            return filter.options[0].value
        case 'date':
            return format(new Date(), 'yyyy-MM-dd').toString()
        case 'number':
        case 'nullable-number':
            if (filter.operators && filter.operators.length > 0) {
                const op = filter.operators[0]
                if (op !== '=') {
                    return { [op]: 0 }
                }
            }
            return 0
        case 'select-component':
            // console.error('do something for this', toJS(filter))
            // throw new Error('Not implemented')
            return ''
        default:
            console.error('Cannot get initial state for filter type', filter)
            throw new Error('Cannot get initial state for filter type')
    }
}

const SavedFilterModal = observer(({ store }: { store: NewFilterStore }) => {
    const [filterName, setFilterName] = useState('')

    const onSave = () => {
        store.saveCustomFilter(filterName, store.currentFilters)
    }

    return (
        <Dialog
            className="p-0"
            isOpen={store.showSaveModal}
            onClose={() => store.setShowSaveModal(false)}
            // nonModal: contains a hover Popover ("Enter a name to save this
            // filter") that portals to body; modal mode disables pointer
            // events on body and breaks the popover.
            nonModal
        >
            <div className="px-3 pt-3 pb-0">
                <h4 className="bp4-heading">New Saved Filter</h4>
            </div>
            <div className="p-3">
                <p className="pb-3">
                    This will create a new saved filter from your currently selected filters. Once
                    saved, you can load a saved filter from the Saved Filters menu
                </p>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                    }}
                >
                    <ControlGroup className={'w-50'} fill={true} vertical={false}>
                        <TextInput
                            value={filterName}
                            onChange={setFilterName}
                            placeholder={'Filter name'}
                            autoFocus
                            onKeyDown={(e) => (e.key === 'Enter' ? onSave() : null)}
                        />
                        <Popover
                            interactionKind="hover"
                            position="bottom"
                            content={<div className="p-2">Enter a name to save this filter</div>}
                            disabled={filterName != ''}
                        >
                            <Button
                                className="ml-2 h-full"
                                disabled={filterName == ''}
                                intent="primary"
                                text="Save"
                                onClick={onSave}
                            />
                        </Popover>
                    </ControlGroup>
                </div>
            </div>
        </Dialog>
    )
})

const ManageFilterModal = observer(({ store }: { store: NewFilterStore }) => {
    const [loading, setLoading] = useState(false)

    const deleteFilter = (filter: SavedFilter) => {
        return () => {
            if (confirm(`Are you sure you want to delete ${filter.name}?`)) {
                store.savedFiltersStore.delete(filter)
            }
        }
    }
    const columns: Column<any, any>[] = [
        {
            key: 'name',
            cell: (item) => {
                return (
                    <EditableField
                        value={item.name}
                        leftIcon={null}
                        type="text"
                        loading={loading}
                        onChange={(name) => {
                            setLoading(true)
                            store.savedFiltersStore
                                .edit({
                                    ...toJS(item),
                                    name,
                                })
                                .then(() => {
                                    setLoading(false)
                                })
                        }}
                    />
                )
            },
        },
        {
            key: 'filters',
            cell: (item) => {
                const dummyStore = new NewFilterStore(
                    store.tableStore,
                    store.savedFiltersStore,
                    store.allowedFilters,
                    false,
                )

                // Initialize the active filters to the saved filter's values
                runInAction(() => {
                    dummyStore.activeFilters = item.filters
                })

                return (
                    <FilterButton
                        editMode
                        scrollable
                        store={dummyStore}
                        allowSavedFilters={false}
                        onSave={() => {
                            store.savedFiltersStore.edit({
                                ...item,
                                filters: dummyStore.activeFilters,
                            })
                        }}
                    />
                )
            },
        },
        {
            key: 'id',
            title: 'Delete',
            cell: (item) => <Button disabled={loading} onClick={deleteFilter(item)} icon="trash" />,
        },
    ]

    return (
        <Dialog
            canOutsideClickClose={false}
            style={{ minWidth: 800 }}
            className="p-0"
            isOpen={store.showManageModal && store.savedFilters.length > 0}
            onClose={() => store.setShowManageModal(false)}
            // nonModal: each row renders an interactive FilterButton whose
            // popover portals to body. Modal mode would disable pointer
            // events on those popovers.
            nonModal
        >
            <div className="px-3 pt-3 pb-0">
                <h4 className="bp4-heading">Manage Saved Filters</h4>
            </div>
            <div className={Classes.DIALOG_BODY}>
                <MyHTMLTable
                    empty={false}
                    paginationClassname={''}
                    htmlTableWrapperClassname={''}
                    reload={() => console.log('reload')}
                    mutateItems={[]}
                    onSelectChange={null}
                    selectKey={null}
                    showPerPage={false}
                    items={store.savedFilters}
                    columns={columns}
                />
            </div>
            <div className={`${Classes.DIALOG_FOOTER} mb-2`}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        intent="none"
                        text="Close"
                        onClick={() => store.setShowManageModal(false)}
                    />
                </div>
            </div>
        </Dialog>
    )
})
